/**
 * Web Modal Component
 *
 */

import React from 'react'
import clsx from 'clsx'
import { isTrue } from '../../../shared/component-helper'
import Section from '../../section/Section'
import ModalContext from '../ModalContext'
import CloseButton from './CloseButton'
import { SectionProps } from '../../Section'

export interface ModalHeaderBarProps
  extends Omit<SectionProps, 'children'> {
  /**
   * The content which will appear when triggering the modal/drawer.
   */
  children?: React.ReactNode

  /**
   * Give the inner content wrapper a class name (maps to `dnb-modal__content__inner`).
   */
  className?: string

  shadowClass?: string
}

interface ModalHeaderBarState {
  showShadow: boolean
}

export default class ModalHeaderBar extends React.PureComponent<
  ModalHeaderBarProps & Omit<React.HTMLProps<HTMLElement>, 'children'>,
  ModalHeaderBarState
> {
  static contextType = ModalContext

  context!: React.ContextType<typeof ModalContext>

  _ref: React.RefObject<any>
  intersectionObserver: IntersectionObserver

  constructor(props) {
    super(props)
    this._ref = React.createRef()
  }

  state = { showShadow: false }

  componentDidMount() {
    this.observeHeader()
  }

  componentDidUpdate(prevProps) {
    // Re-observe if children change
    // This is necessary to handle dynamic content changes
    // that might affect the header's height
    // e.g., when the modal content changes
    if (prevProps.children !== this.props.children) {
      this.intersectionObserver?.disconnect()
      this.observeHeader()
    }
  }

  componentWillUnmount() {
    this.intersectionObserver?.disconnect()
  }

  observeHeader() {
    if (
      typeof window !== 'undefined' &&
      typeof IntersectionObserver !== 'undefined' &&
      this._ref.current
    ) {
      const marginTop = -this._ref.current.clientHeight

      // Find the scroll container (look for .dnb-scroll-view ancestor)
      let scrollRoot = null
      let element = this._ref.current.parentElement
      while (element) {
        if (element.classList?.contains('dnb-scroll-view')) {
          scrollRoot = element
          break
        }
        element = element.parentElement
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries
          this.setState({
            showShadow: !entry.isIntersecting,
          })
        },
        {
          root: scrollRoot,
          rootMargin: `${marginTop}px 0px 0px 0px`,
          threshold: 0.001,
        }
      )

      this.intersectionObserver.observe(this._ref.current)
    }
  }

  render() {
    const {
      className = null,
      children = null,
      ref, //eslint-disable-line
      shadowClass = null,
      ...props
    } = this.props
    const { showShadow } = this.state
    const {
      hideCloseButton = false,
      closeButtonAttributes,
      onCloseClickHandler,
      closeTitle,
    } = this.context

    return (
      <Section
        style_type="white"
        className={clsx(
          'dnb-modal__header__bar',
          showShadow && shadowClass,
          className
        )}
        innerRef={this._ref}
        {...props}
      >
        <div className="dnb-modal__header__bar__inner">
          {children as React.ReactNode}
        </div>

        {!isTrue(hideCloseButton) && (
          <div className="dnb-modal__header__bar__close">
            <CloseButton
              onClick={onCloseClickHandler}
              closeTitle={closeTitle}
              {...closeButtonAttributes}
            />
          </div>
        )}
      </Section>
    )
  }
}
